import {
    ActionIcon,
    Box,
    Container,
    Flex,
    Group,
    Loader,
    Tooltip,
} from '@mantine/core';
import { CreateMessageGroupModal, EditMessageModal } from '../modals';
import { GroupExpandToolbar, StatColumn } from '../../../common';
import { IconChartDots2, IconPencil } from '@tabler/icons-react';
import {
    IdColumn,
    MessageContentColumn,
    TypeColumn,
    VariationsColumn,
} from './columns';
import { useDisclosure, useToggle } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';

import CampaignGroupStatsTimelineModal from '../../../modals/timeline/CampaignGroupStatsTimelineModal';
import CheckboxColumn from '../../../common/tables/columns/CheckboxColumn';
import { MantineReactTable } from 'mantine-react-table';
import MessageTableToolbar from '../toolbars/MessagesToolbarActions';
import classes from '../GroupTable.module.css';
import { hasRole } from '../../../../utils/roles';
import { naiveRound } from '../../../../utils/text';
import { sumObjectKeys } from '../../../../utils/statUtils';
import { useAuthStore } from '../../../../store';
import { useForm } from '@mantine/form';
import useGetMessageGroupByIdQuery from '../../../../http/queries/groups/useGetMessageGroupById';
import useToggleGroupProperty from '../../../../http/mutations/groups/useToggleGroupProperty';

const MessageTable = ({ groupId }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const [rowSelection, setRowSelection] = useState({});
    const [tableData, setTableData] = useState([]);
    const [showDeleted, toggleShowDeleted] = useToggle();

    const [modalRow, setModalRow] = useState(null);
    const [
        timelineModalOpen,
        { open: openTimelineModal, close: closeTimelineModal },
    ] = useDisclosure();

    const { isLoading, data: messageGroup } = useGetMessageGroupByIdQuery(
        groupId,
        groupId != null
    );

    const messages = messageGroup?.messages;
    useEffect(() => {
        if (!messages) {
            return;
        }

        if (showDeleted) {
            setTableData(messages);
        } else {
            setTableData(messages.filter((x) => !x.deleted));
        }
    }, [messages, showDeleted]);

    const form = useForm({
        initialValues: {},

        validate: {
            /*emailOrUsername: isNotEmpty('Username or email is required'),
                    password: (val) =>
                        val.length <= 6
                            ? 'Password should include at least 6 characters'
                            : null,*/
        },
    });

    const { mutate: setActive } = useToggleGroupProperty({
        type: 'messages',
        property: 'active',
    });

    const { mutate: setApproved } = useToggleGroupProperty({
        type: 'messages',
        property: 'approved',
    });

    const { mutate: setDeleted } = useToggleGroupProperty({
        type: 'messages',
        property: 'deleted',
    });

    const handleActiveChange = (rowData, value, e) => {
        setActive({
            id: rowData.id,
            value: value,
        });
    };

    const handleApprovedChanged = (rowData, value, e) => {
        setApproved({
            id: rowData.id,
            value: value,
        });
    };

    const handleDeleteChange = (rowData, value) => {
        setDeleted({
            id: rowData.id,
            value: value,
        });
    };

    const columns = useMemo(() => {
        const columns = [
            IdColumn(),
            MessageContentColumn(),
            TypeColumn(),
            VariationsColumn(),
            StatColumn('Sent', (x) => x.stats?.send),
            StatColumn('Clicks', (x) =>
                sumObjectKeys(x.stats, ['uniqueClick'])
            ),
            StatColumn('Previews', (x) =>
                sumObjectKeys(x.stats, ['uniquePreview'])
            ),
            StatColumn(
                'CTR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((uniqueClicks / x.stats?.send) * 100, 2);
                },
                true
            ),
            StatColumn('Conversions', (x) => x.stats?.conversion),
            StatColumn(
                'CR',
                (x) => {
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound(
                        (x.stats?.conversion / uniqueClicks) * 100,
                        2
                    );
                },
                true
            ),
            CheckboxColumn({
                header: 'Active',
                accessorKey: 'active',
                onChange: handleActiveChange,
            }),
            hasRole(userProfile, 'SUPER_ADMIN')
                ? CheckboxColumn({
                      header: 'Approved',
                      accessorKey: 'approved',
                      onChange: handleApprovedChanged,
                  })
                : null,
            CheckboxColumn({
                header: 'Hide',
                accessorKey: 'deleted',
                onChange: handleDeleteChange,
            }),
            /*PropertyColumn({
                form,
                availableFilters,
                isLoading,
            }),
            OperatorColumn({
                form,
                availableFilters,
                isLoading,
            }),
            ValuesColumn({ form, availableFilters, isLoading }),*/
        ];
        return columns.filter((x) => x != null);
    }, [isLoading, userProfile]);

    const [editModalOpen, { open: openEditModal, close: closeEditModal }] =
        useDisclosure();

    const handleEdit = (rowData) => {
        setModalRow(rowData);
        openEditModal();
    };

    const handleTimeline = (rowData) => {
        setModalRow(rowData);
        openTimelineModal();
    };

    if (isLoading) {
        return (
            <Group
                w={'100%'}
                mih={100}
                justify={'center'}>
                <Loader />
            </Group>
        );
    }

    const tableProps = {
        //expandRowsFn: true, // Assuming this should be true, since it was provided without a value
        columns: columns,
        data: tableData,
        enableSorting: false,
        enableColumnActions: false,
        enableDensityToggle: false,
        layoutMode: 'semantic',

        editDisplayMode: 'row',
        createDisplayMode: 'row',
        enableHiding: false,
        enableFilters: false,
        enableEditing: true,
        enableResizing: true,
        enableFullScreenToggle: false,
        defaultColumn: {
            minSize: 0,
            maxSize: 0,
        },

        state: { rowSelection },
        mantineTopToolbarProps: () => ({
            className: classes.toolbar,
        }),
        enableTopToolbar: true,
        renderTopToolbarCustomActions: ({ props: table }) => (
            <MessageTableToolbar
                messageGroup={messageGroup}
                table={table}
                showHidden={showDeleted}
                toggleShowHidden={toggleShowDeleted}
            />
        ),
        enablePagination: true,
        manualPagination: false,
        enableBottomToolbar: true,
        enableRowActions: true,
        positionActionsColumn: 'last',
        positionExpandColumn: 'last',
        mantineTableContainerProps: {
            style: {
                minHeight: '100px',
                maxWidth: '100%',
            },
        },
        mantineTableBodyRowProps: ({
            isDetailPanel,
            row,
            staticRowIndex,
            table,
        }) => {
            const edited = form.isDirty(`filters.${row.index}`);
            const created = row.index >= messages.length;
            return {
                className: classes.rowBox,
                /*style: {
                    '--gradientEnd': '100%',
                },*/
                mod: {
                    edited: edited && !created,
                    created,
                },
            };
        },

        mantineTableBodyCellProps: ({
            isDetailPanel,
            row,
            staticRowIndex,
            table,
        }) => {
            const edited = form.isDirty(`filters.${row.index}`);
            const created = row.index >= messages.length;
            return {
                className: classes.cellBox,
                mod: {
                    edited: edited && !created,
                    deleted: row.getIsSelected(),
                    created,
                },
            };
        },

        renderRowActions: ({ table, row }) => (
            <>
                <Tooltip label={'Edit Message'}>
                    <ActionIcon
                        variant='default'
                        size={'sm'}
                        onClick={() => handleEdit(row.original)}>
                        <IconPencil size={16} />
                    </ActionIcon>
                </Tooltip>

                <Tooltip label={'Send Timeline'}>
                    <ActionIcon
                        variant='default'
                        size={'sm'}
                        onClick={() => handleTimeline(row.original)}>
                        <IconChartDots2 size={16} />
                    </ActionIcon>
                </Tooltip>
            </>
        ),
        state: {
            density: 'xs',
        },
        meta: {
            dataLength: tableData.length,
            originalLength: messages.length,
        },
    };
    return (
        <>
            <MantineReactTable {...tableProps} />
            <EditMessageModal
                messageGroup={messageGroup}
                existing={modalRow}
                opened={editModalOpen}
                close={closeEditModal}
            />
            <CampaignGroupStatsTimelineModal
                resource={'messages'}
                resourceId={modalRow?.id}
                title={`Send Timeline - Message ID ${modalRow?.id}`}
                opened={timelineModalOpen}
                close={closeTimelineModal}
            />
        </>
    );
};
export default MessageTable;
